import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
    SealCheck, Crown, ChalkboardTeacher, Star, Package,
    Check, ArrowRight,
} from "@phosphor-icons/react";

/**
 * "Your Unlocks" — a unified snapshot of every candidate upsell
 * (Verified · Pro · Prep · Featured · All-in-One Bundle) on the
 * candidate dashboard. One request, five tiles, deep-links to each
 * individual purchase page. Keeps monetization surface visible on
 * every session without polluting each product page.
 */
const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short" }); }
    catch { return null; }
};
const daysLeft = (iso) => {
    if (!iso) return 0;
    try {
        const diff = new Date(iso).getTime() - Date.now();
        return Math.max(0, Math.floor(diff / 86400000));
    } catch { return 0; }
};

function Tile({ icon: Icon, label, to, active, statusLine, cta, tone, testid }) {
    return (
        <Link to={to} data-testid={testid}
              className={`group relative flex flex-col justify-between p-4 border ${active ? tone.activeBorder : "border-[#E5E5E5]"} bg-white hover:border-ink transition-colors min-h-[128px]`}>
            <div className="flex items-start justify-between gap-2">
                <div className={`w-9 h-9 flex items-center justify-center ${active ? tone.activeBg : "bg-[#F5F5F5]"}`}>
                    <Icon size={16} weight="fill" className={active ? tone.activeIcon : "text-[#A3A3A3]"} />
                </div>
                {active && (
                    <span className={`inline-flex items-center gap-1 text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 ${tone.pillBg} ${tone.pillText}`}>
                        <Check size={9} weight="bold" /> Active
                    </span>
                )}
            </div>
            <div>
                <p className="font-heading text-sm font-black text-ink leading-tight">{label}</p>
                <p className="text-[10px] text-[#525252] leading-tight mt-1 min-h-[14px]">{statusLine}</p>
                <p className="text-[10px] font-black uppercase tracking-wider text-cobalt group-hover:text-ink mt-2 inline-flex items-center gap-1">
                    {cta} <ArrowRight size={10} weight="bold" />
                </p>
            </div>
        </Link>
    );
}

export default function CandidateUnlocksCard() {
    const [state, setState] = useState(null);
    useEffect(() => {
        // Bundle-status conveniently returns every upsell's current value.
        api.get("/me/bundle/all-four/status")
            .then((r) => setState(r.data))
            .catch(() => setState(null));
    }, []);

    if (!state) return null;
    const cs = state.current_state || {};
    const verified = !!cs.verified;
    const proActive = cs.pro_until && cs.pro_until > new Date().toISOString();
    const proDays = daysLeft(cs.pro_until);
    const featuredActive = cs.featured_until && cs.featured_until > new Date().toISOString();
    const featuredDays = daysLeft(cs.featured_until);
    const prepCredits = Number(cs.prep_credits || 0);
    const bundleBought = state.latest_order?.status === "paid";

    const TONES = {
        verified: { activeBorder: "border-emerald-500", activeBg: "bg-emerald-50", activeIcon: "text-emerald-600", pillBg: "bg-emerald-500", pillText: "text-white" },
        pro:      { activeBorder: "border-cobalt", activeBg: "bg-blue-50", activeIcon: "text-cobalt", pillBg: "bg-cobalt", pillText: "text-white" },
        prep:     { activeBorder: "border-splash", activeBg: "bg-splash/10", activeIcon: "text-splash", pillBg: "bg-splash", pillText: "text-ink" },
        featured: { activeBorder: "border-amber-500", activeBg: "bg-amber-50", activeIcon: "text-amber-600", pillBg: "bg-amber-500", pillText: "text-white" },
        bundle:   { activeBorder: "border-ink", activeBg: "bg-ink", activeIcon: "text-splash", pillBg: "bg-ink", pillText: "text-splash" },
    };

    return (
        <div data-testid="unlocks-card" className="mb-6">
            <div className="flex items-baseline justify-between mb-3">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.25em] text-[#A3A3A3]">Your unlocks</p>
                    <p className="font-heading text-lg font-black text-ink">Boosts &amp; premium status</p>
                </div>
                <Link to="/candidate/bundle" data-testid="unlocks-bundle-link"
                      className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-ink inline-flex items-center gap-1">
                    All 4 for ₹999 · save 41% <ArrowRight size={10} weight="bold" />
                </Link>
            </div>
            <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
                <Tile testid="unlock-verified" icon={SealCheck} tone={TONES.verified}
                      label="Verified Badge" to="/candidate/verified" active={verified}
                      statusLine={verified ? "KYC approved · 3× views" : "Prove you're real"}
                      cta={verified ? "Manage" : "Get verified · ₹299"} />
                <Tile testid="unlock-pro" icon={Crown} tone={TONES.pro}
                      label="Pro Pass" to="/candidate/subscriptions" active={proActive}
                      statusLine={proActive ? `${proDays}d left · until ${fmtDate(cs.pro_until)}` : "Unlimited apply + AI rewrite"}
                      cta={proActive ? "Extend" : "Go Pro · ₹499/mo"} />
                <Tile testid="unlock-prep" icon={ChalkboardTeacher} tone={TONES.prep}
                      label="Interview Prep" to="/candidate/interview-prep" active={prepCredits > 0}
                      statusLine={prepCredits > 0 ? `${prepCredits} credit${prepCredits === 1 ? "" : "s"} ready` : "AI mock interviews"}
                      cta={prepCredits > 0 ? "Start a session" : "Buy prep · ₹199"} />
                <Tile testid="unlock-featured" icon={Star} tone={TONES.featured}
                      label="Featured" to="/candidate/featured" active={featuredActive}
                      statusLine={featuredActive ? `${featuredDays}d left · gold star` : "Top of every search"}
                      cta={featuredActive ? "Extend · ₹99" : "Get featured · ₹99"} />
                <Tile testid="unlock-bundle" icon={Package} tone={TONES.bundle}
                      label="All-in-One" to="/candidate/bundle" active={bundleBought}
                      statusLine={bundleBought ? "All 4 unlocked ✨" : `Save ₹${state.savings_inr || 697} · get all 4`}
                      cta={bundleBought ? "View status" : "Unlock ₹999"} />
            </div>
        </div>
    );
}
